import { useEffect, useState, useCallback } from "react";

const DISMISS_KEY = "confirmph_install_dismissed_at";
const DISMISS_DAYS = 14; // re-show the floating prompt after 14 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  const mq = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  // iOS Safari exposes navigator.standalone
  const ios = !!window.navigator.standalone;
  return mq || ios;
}

function isIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const isIDevice = /iphone|ipad|ipod/i.test(ua);
  // iPadOS reports MacIntel + touch
  const isIPadOS = ua.includes("Macintosh") && "ontouchend" in document;
  return isIDevice || isIPadOS;
}

function isRecentlyDismissed() {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const when = Number(v);
    if (!when) return false;
    return (Date.now() - when) < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch (_e) {
    return false;
  }
}

/**
 * useInstallPrompt: listens for the browser's `beforeinstallprompt`
 * event so we can trigger the native install dialog from our own UI.
 *
 * Returns:
 *   - canInstall  : true when the browser supports installation now
 *   - installed   : true once the app has been installed (or is running standalone)
 *   - isIOS       : true on iOS, where we must show manual instructions instead
 *   - promptInstall(): triggers the browser dialog and resolves to the outcome
 *   - dismiss()   : remember the user dismissed the floating banner
 *   - dismissed   : whether the banner has been dismissed recently
 */
export default function useInstallPrompt() {
  const [deferred, setDeferred] = useState(() => (
    typeof window !== "undefined" ? window.__confirmphInstallEvent || null : null
  ));
  const [installed, setInstalled] = useState(isStandalone());
  const [dismissed, setDismissed] = useState(isRecentlyDismissed());

  useEffect(() => {
    function onBeforeInstall(e) {
      e.preventDefault();
      setDeferred(e);
    }
    function onAvailable() {
      if (typeof window !== "undefined" && window.__confirmphInstallEvent) {
        setDeferred(window.__confirmphInstallEvent);
      }
    }
    function onInstalled() {
      setInstalled(true);
      setDeferred(null);
      if (typeof window !== "undefined") window.__confirmphInstallEvent = null;
    }
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("confirmph:installavailable", onAvailable);
    window.addEventListener("confirmph:installed", onInstalled);

    // Also update if display-mode changes (rare but possible)
    const mq = window.matchMedia && window.matchMedia("(display-mode: standalone)");
    const onChange = () => setInstalled(isStandalone());
    if (mq && mq.addEventListener) mq.addEventListener("change", onChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("confirmph:installavailable", onAvailable);
      window.removeEventListener("confirmph:installed", onInstalled);
      if (mq && mq.removeEventListener) mq.removeEventListener("change", onChange);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return { outcome: "unavailable" };
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      setDeferred(null);
      if (typeof window !== "undefined") window.__confirmphInstallEvent = null;
      if (choice.outcome === "accepted") setInstalled(true);
      return choice;
    } catch (err) {
      // Some browsers throw if prompt() is called more than once.
      setDeferred(null);
      if (typeof window !== "undefined") window.__confirmphInstallEvent = null;
      return { outcome: "unavailable", error: err };
    }
  }, [deferred]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_e) { /* ignore quota */ }
    setDismissed(true);
  }, []);

  return {
    canInstall: !!deferred && !installed,
    installed,
    isIOS: isIOS() && !installed,
    promptInstall,
    dismiss,
    dismissed,
  };
}
